import { createContext, useState, useEffect, ReactNode, useMemo } from 'react'

// Utility function to get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Define the context type interface
interface ThemeContextType {
  currentTheme: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark' | null) => void
}

export const ThemeContext = createContext<ThemeContextType>({
  currentTheme: 'light',
  setTheme: () => {},
})

interface ThemeProviderProps {
  contentfulDarkModeSet?: boolean
  children: ReactNode
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Server-side: default to light
    if (typeof window === 'undefined') return 'light'
    const stored = localStorage.getItem('theme')
    if (stored === '"dark"') {
      return 'dark'
    } else if (stored === '"light"') {
      return 'light'
    } else {
      return getSystemTheme()
    }
  })

  // Function to update theme and localStorage
  const setTheme = (theme: 'light' | 'dark' | null) => {
    if (theme === null) {
      localStorage.removeItem('theme')
      setCurrentTheme(getSystemTheme())
    } else {
      localStorage.setItem('theme', JSON.stringify(theme))
      setCurrentTheme(theme)
    }
  }

  // Apply theme to DOM immediately without delays
  useEffect(() => {
    document.documentElement.classList.toggle('dark', currentTheme === 'dark')
  }, [currentTheme])

  // handle theme switcher
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'theme') return

      try {
        const stored = e.newValue
        if (stored === '"dark"') {
          setCurrentTheme('dark')
        } else if (stored === '"light"') {
          setCurrentTheme('light')
        } else if (stored === null) {
          // Theme was cleared, use system preference
          setCurrentTheme(getSystemTheme())
        }
      } catch {
        console.log('Error accessing localStorage')
      }
    }

    // Only listen for storage changes from other tabs
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const contextValue = useMemo(
    () => ({
      currentTheme,
      setTheme,
    }),
    [currentTheme, setTheme]
  )

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>
}
